import dayjs from 'dayjs';

import type {
  MarketingRouterExperimentDecision,
  MarketingRouterMetricResult,
  MarketingRouterSignificance,
} from '@luxuryescapes/contract-svc-promo/marketing-router/admin';

/**
 * Pure display mapping for the marketing router experiment detail page.
 * The svc-promo significance endpoint deliberately returns codes and raw
 * statistics with no copy — every marketer-facing wording lives here so it
 * can be unit tested without rendering.
 */

export type MarketingRouterMetricKey = MarketingRouterMetricResult['metric'];

export const METRIC_DISPLAY: Record<
  MarketingRouterMetricKey,
  { name: string; perVisitorLabel: string; totalLabel: string }
> = {
  purchase: { name: 'Purchase rate', perVisitorLabel: 'Purchase rate', totalLabel: 'Purchases' },
  ttv: { name: 'TTV', perVisitorLabel: 'TTV per visitor', totalLabel: 'Total TTV' },
  margin: { name: 'Margin', perVisitorLabel: 'Margin per visitor', totalLabel: 'Total margin' },
};

const audFormatter = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });
const countFormatter = new Intl.NumberFormat('en-AU', { maximumFractionDigits: 0 });

export function formatMetricValue(metric: MarketingRouterMetricKey, value: number): string {
  if (metric === 'purchase') {
    return `${(value * 100).toFixed(2)}%`;
  }
  return audFormatter.format(value);
}

export function formatMetricTotal(metric: MarketingRouterMetricKey, value: number): string {
  if (metric === 'purchase') {
    return countFormatter.format(Math.round(value));
  }
  return audFormatter.format(value);
}

export function formatVisitors(value: number): string {
  return countFormatter.format(value);
}

export function formatRelativeLift(relativeLift: number | null): string {
  if (relativeLift === null || !Number.isFinite(relativeLift)) {
    return '—';
  }
  const percent = relativeLift * 100;
  const sign = percent > 0 ? '+' : '';
  return `${sign}${percent.toFixed(1)}%`;
}

/**
 * Collapses the relative 95% CI into a single "give or take" number: half the
 * interval width, as a percentage. Null when the backend could not compute a
 * relative interval (zero base value).
 */
export function getLiftMarginOfErrorPercent(relativeCi95: number[] | null): number | null {
  if (!relativeCi95 || relativeCi95.length !== 2) {
    return null;
  }
  const [lower, upper] = relativeCi95;
  if (!Number.isFinite(lower) || !Number.isFinite(upper)) {
    return null;
  }
  return ((upper - lower) / 2) * 100;
}

/** (1 - adjusted p) as a percentage, or null when the test produced no p-value. */
export function getConfidencePercent(result: Pick<MarketingRouterMetricResult, 'pAdjusted'>): number | null {
  return Number.isFinite(result.pAdjusted) ? (1 - result.pAdjusted) * 100 : null;
}

/**
 * Optimizely-style single confidence number: (1 - p) as a display percent.
 * Shown while still collecting too — the number is real, just not yet
 * decisive, and hiding it made the page look broken.
 */
export function formatConfidence(result: Pick<MarketingRouterMetricResult, 'pAdjusted'>): string {
  const confidence = getConfidencePercent(result);
  if (confidence === null) {
    return '—';
  }
  if (confidence >= 99) {
    return '>99%';
  }
  if (confidence <= 1) {
    return '<1%';
  }
  return `${confidence.toFixed(0)}%`;
}

/** Confidence for any p-value, e.g. the sample-ratio check. */
export function formatConfidenceFromP(pValue: number): string {
  return formatConfidence({ pAdjusted: pValue });
}

export type RuleRunStatus = {
  label: 'Scheduled' | 'Running' | 'Ended';
  color: 'default' | 'success' | 'info';
};

export function getRuleRunStatus(startDate: string, endDate: string, now: dayjs.Dayjs): RuleRunStatus {
  if (now.isBefore(dayjs(startDate), 'day')) {
    return { label: 'Scheduled', color: 'default' };
  }
  if (now.isAfter(dayjs(endDate), 'day')) {
    return { label: 'Ended', color: 'info' };
  }
  return { label: 'Running', color: 'success' };
}

/**
 * Duration copy for the header tile: "Day 12 of 30" while running, with
 * plain-language variants either side of the window.
 */
export function formatDuration(startDate: string, endDate: string, now: dayjs.Dayjs): string {
  const start = dayjs(startDate).startOf('day');
  const end = dayjs(endDate).startOf('day');
  const today = now.startOf('day');
  // Both endpoints inclusive: a rule running 1st..30th is a 30-day experiment.
  const totalDays = Math.max(end.diff(start, 'day') + 1, 1);

  if (today.isBefore(start)) {
    const daysUntil = start.diff(today, 'day');
    return daysUntil === 1 ? 'Starts tomorrow' : `Starts in ${daysUntil} days`;
  }
  if (today.isAfter(end)) {
    return `Ran ${totalDays} ${totalDays === 1 ? 'day' : 'days'}`;
  }
  const dayNumber = Math.min(today.diff(start, 'day') + 1, totalDays);
  return `Day ${dayNumber} of ${totalDays}`;
}

export function formatTrafficAllocation(weights: number[]): string {
  return weights.map((weight) => `${weight}%`).join(' / ');
}

export type DecisionDisplay = {
  severity: 'success' | 'warning' | 'error' | 'info';
  title: string;
  description: string;
};

const numberOfVisitors = (count: number): string => `${formatVisitors(count)} visitors`;

/**
 * Maps a ship/no-ship decision code to marketer-facing copy. Kept free of
 * statistics jargon on purpose — the numbers backing it live in the metric
 * tables below the decision card.
 */
export function getDecisionDisplay(
  decision: MarketingRouterExperimentDecision,
  baseVariantName: string,
): DecisionDisplay {
  const metricName = METRIC_DISPLAY[decision.primaryMetric].name.toLowerCase();
  const lift = formatRelativeLift(decision.primaryRelativeLift);
  const degraded = decision.degradedSecondaryMetrics
    .map((metric) => METRIC_DISPLAY[metric].name.toLowerCase())
    .join(' and ');

  switch (decision.code) {
    case 'SHIP':
      return {
        severity: 'success',
        title: `Ship ${decision.variantName}`,
        description: `${decision.variantName} beat ${baseVariantName} on ${metricName} (${lift}) and nothing got significantly worse.`,
      };
    case 'SHIP_WITH_TRADEOFFS':
      return {
        severity: 'warning',
        title: `${decision.variantName} wins on ${metricName}, with trade-offs`,
        description: `${decision.variantName} improved ${metricName} by ${lift} vs ${baseVariantName}, but ${degraded} got significantly worse. Decide whether the trade-off is acceptable before shipping.`,
      };
    case 'DO_NOT_SHIP':
      return {
        severity: 'error',
        title: `Keep ${baseVariantName}`,
        description: `${decision.variantName} performed significantly worse than ${baseVariantName} on ${metricName} (${lift}). Do not ship it.`,
      };
    case 'COLLECTING':
      return {
        severity: 'info',
        title: 'Still collecting data',
        description:
          decision.remainingSessions !== undefined && decision.remainingSessions > 0
            ? `${decision.variantName} needs roughly ${numberOfVisitors(decision.remainingSessions)} more before a reliable call can be made.`
            : `${decision.variantName} does not have enough visitors yet for a reliable call.`,
      };
    case 'INVALID_SRM':
      return {
        severity: 'error',
        title: 'Results cannot be trusted',
        description:
          'Traffic is not splitting between variants the way the rule is configured, so every number on this page is unreliable. Check the rule setup and targeting before reading anything into the results.',
      };
    case 'INCONCLUSIVE':
      return {
        severity: 'info',
        title: 'No clear winner yet',
        description: `The difference between ${decision.variantName} and ${baseVariantName} on ${metricName} (${lift}) is too small to be confident it is real. Keep the experiment running or call it a draw.`,
      };
  }
}

export type VariantVisitorsSummary = {
  variantId: string;
  variantName: string;
  visitors: number;
  isControl: boolean;
};

/**
 * Per-variant analytical visitor counts, derived from the metric results
 * (baseN/treatN are constant across metrics for a given variant pair).
 * Control first, then treatments in result order.
 */
export function summariseVariantVisitors(
  results: MarketingRouterMetricResult[],
  baseVariantId: string,
  baseVariantName: string,
): VariantVisitorsSummary[] {
  const summaries: VariantVisitorsSummary[] = [];
  if (results.length > 0) {
    summaries.push({
      variantId: baseVariantId,
      variantName: baseVariantName,
      visitors: results[0].baseN,
      isControl: true,
    });
  }
  const seenTreatmentIds = new Set<string>();
  for (const result of results) {
    if (seenTreatmentIds.has(result.variantId)) {
      continue;
    }
    seenTreatmentIds.add(result.variantId);
    summaries.push({
      variantId: result.variantId,
      variantName: result.variantName,
      visitors: result.treatN,
      isControl: false,
    });
  }
  return summaries;
}

export function totalVisitors(summaries: VariantVisitorsSummary[]): number {
  return summaries.reduce((total, summary) => total + summary.visitors, 0);
}

/**
 * Days the experiment has been live so far, both ends inclusive and never
 * past its end date. Used for "after N days" copy and traffic-rate estimates.
 */
export function getDaysRun(startDate: string, endDate: string, now: dayjs.Dayjs): number {
  const start = dayjs(startDate).startOf('day');
  const end = dayjs(endDate).startOf('day');
  const today = now.startOf('day');
  if (today.isBefore(start)) {
    return 0;
  }
  const last = today.isAfter(end) ? end : today;
  return last.diff(start, 'day') + 1;
}

const Z_ALPHA_TWO_SIDED = 1.96;
const Z_POWER_80 = 0.8416;

/**
 * The relative standard error implied by the 95% CI: half its width over z.
 * The endpoint does not return SE directly, but the interval encodes it.
 */
function getRelativeStandardError(result: MarketingRouterMetricResult): number | null {
  const ci = result.relativeCi95;
  if (!ci || ci.length !== 2 || !Number.isFinite(ci[0]) || !Number.isFinite(ci[1])) {
    return null;
  }
  return (ci[1] - ci[0]) / (2 * Z_ALPHA_TWO_SIDED);
}

/**
 * The smallest relative effect the experiment could detect right now at 80%
 * power and α = 0.05 (two-sided): (z_α + z_β) × SE. Anything inside this band
 * is indistinguishable from zero at current traffic, whatever the point
 * estimate says.
 */
export function getDetectableRelativeEffect(result: MarketingRouterMetricResult): number | null {
  const standardError = getRelativeStandardError(result);
  return standardError === null ? null : (Z_ALPHA_TWO_SIDED + Z_POWER_80) * standardError;
}

export type PowerRuntimeRow = {
  /** Relative effect size, e.g. 0.2 for ±20%. */
  targetEffect: number;
  visitorsPerArm: number;
  /** Plain-language time to reach that many visitors at the current rate. */
  runtime: string;
  detectableNow: boolean;
};

function formatRuntime(days: number): string {
  if (days <= 0) {
    return 'now';
  }
  if (days < 14) {
    return `~${Math.ceil(days)} days`;
  }
  if (days < 60) {
    return `~${Math.round(days / 7)} weeks`;
  }
  if (days < 540) {
    return `~${Math.round(days / 30)} months`;
  }
  return 'over a year';
}

/**
 * How many visitors per arm it takes to detect each target effect, and how
 * long that is at the traffic seen so far. SE shrinks with √N, so the count
 * scales from today's detectable effect: N × (detectable / target)².
 */
export function getPowerRuntimeRows(
  result: MarketingRouterMetricResult,
  daysRun: number,
  targets: number[] = [0.3, 0.2, 0.1],
): PowerRuntimeRow[] {
  const detectable = getDetectableRelativeEffect(result);
  const perArm = Math.min(result.baseN, result.treatN);
  if (detectable === null || perArm <= 0 || daysRun <= 0) {
    return [];
  }
  const visitorsPerDay = perArm / daysRun;
  const rows: PowerRuntimeRow[] = [
    { targetEffect: detectable, visitorsPerArm: perArm, runtime: 'now', detectableNow: true },
  ];
  for (const target of targets) {
    if (target >= detectable) {
      continue;
    }
    const needed = Math.ceil(perArm * (detectable / target) ** 2);
    rows.push({
      targetEffect: target,
      visitorsPerArm: needed,
      runtime: formatRuntime((needed - perArm) / visitorsPerDay),
      detectableNow: false,
    });
  }
  return rows;
}

export function formatEffectSize(relative: number): string {
  return `±${(relative * 100).toFixed(0)}%`;
}

function formatRelativeCi(relativeCi95: number[] | null): string | null {
  if (!relativeCi95 || relativeCi95.length !== 2) {
    return null;
  }
  return `${formatRelativeLift(relativeCi95[0])} to ${formatRelativeLift(relativeCi95[1])}`;
}

/**
 * The bullets under one metric's numbers: what the reading is, how sure we
 * are, and what the experiment could even detect. Derived from the data only,
 * so it never claims more than the endpoint measured.
 */
export function getMetricRead(result: MarketingRouterMetricResult, daysRun: number): string[] {
  const name = METRIC_DISPLAY[result.metric].name;
  const lift = formatRelativeLift(result.relativeLift);
  const confidence = formatConfidence(result);
  const ci = formatRelativeCi(result.relativeCi95);
  const detectable = getDetectableRelativeEffect(result);
  const after = daysRun > 0 ? ` after ${daysRun} ${daysRun === 1 ? 'day' : 'days'}` : '';
  const bullets: string[] = [];

  if (result.invalidReason === 'SRM_FAILURE') {
    bullets.push('Traffic is not splitting as configured, so this reading cannot be trusted.');
    return bullets;
  }

  if (result.verdict === 'SIGNIFICANT') {
    bullets.push(
      `${name} is ${result.absoluteLift >= 0 ? 'up' : 'down'} ${lift} on ${result.variantName}${after} — significant at ${confidence} confidence.`,
    );
  } else if (result.verdict === 'COLLECTING') {
    bullets.push(
      `${name} shows ${lift} on ${result.variantName}${after}, but there are not enough visitors yet to read it.`,
    );
  } else {
    bullets.push(
      `${name} shows ${lift} on ${result.variantName}${after} — not significant (${confidence} confidence, 95% needed).`,
    );
  }

  if (ci) {
    bullets.push(
      `The true effect is somewhere between ${ci}${ci.includes('-') && ci.includes('+') ? ', which includes zero' : ''}.`,
    );
  }

  if (detectable !== null && result.verdict !== 'SIGNIFICANT') {
    bullets.push(
      `At current traffic only an effect of ${formatEffectSize(detectable)} or more would be detectable; anything smaller looks like noise for now.`,
    );
  }

  return bullets;
}

export type DecisionSummary = {
  severity: DecisionDisplay['severity'];
  title: string;
  bullets: string[];
};

/**
 * The decision as a headline plus one bullet per metric and one for what to
 * do next. The headline says what happened to the metrics; the code-based
 * `getDecisionDisplay` still supplies severity and the ship/no-ship framing.
 */
export function getDecisionSummary(
  decision: MarketingRouterExperimentDecision,
  results: MarketingRouterMetricResult[],
  baseVariantName: string,
  daysRun: number,
): DecisionSummary {
  const display = getDecisionDisplay(decision, baseVariantName);
  const variantResults = results.filter((result) => result.variantId === decision.variantId);
  const primary = variantResults.find((result) => result.metric === decision.primaryMetric);
  const metricNames = variantResults.map((result) => METRIC_DISPLAY[result.metric].name.toLowerCase());
  const primaryName = METRIC_DISPLAY[decision.primaryMetric].name.toLowerCase();
  const lift = formatRelativeLift(decision.primaryRelativeLift);
  const after = daysRun > 0 ? ` after ${daysRun} ${daysRun === 1 ? 'day' : 'days'}` : '';
  const listNames = (names: string[]) =>
    names.length > 1 ? `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}` : (names[0] ?? primaryName);

  let title: string;
  switch (decision.code) {
    case 'SHIP':
      title = `${decision.variantName} lifts ${primaryName} by ${lift} — ship it.`;
      break;
    case 'SHIP_WITH_TRADEOFFS':
      title = `${decision.variantName} lifts ${primaryName} by ${lift}, but ${decision.degradedSecondaryMetrics
        .map((metric) => METRIC_DISPLAY[metric].name.toLowerCase())
        .join(' and ')} got worse.`;
      break;
    case 'DO_NOT_SHIP':
      title = `${decision.variantName} lowers ${primaryName} by ${lift} — keep ${baseVariantName}.`;
      break;
    case 'INVALID_SRM':
      title = 'Results cannot be trusted — traffic is not splitting as configured.';
      break;
    default: {
      const anySignificant = variantResults.some((result) => result.verdict === 'SIGNIFICANT');
      title = anySignificant
        ? `No clear call on ${primaryName} yet${after}.`
        : `No detectable effect on ${listNames(metricNames)}${after}.`;
    }
  }

  const bullets: string[] = [];
  if (decision.code !== 'INVALID_SRM') {
    for (const result of variantResults) {
      const name = METRIC_DISPLAY[result.metric].name;
      const resultLift = formatRelativeLift(result.relativeLift);
      const confidence = formatConfidence(result);
      if (result.verdict === 'SIGNIFICANT') {
        bullets.push(`${name}: ${resultLift}, significant (${confidence} confidence).`);
      } else if (result.verdict === 'COLLECTING') {
        bullets.push(`${name}: ${resultLift}, too few visitors to read yet (${confidence} confidence).`);
      } else {
        bullets.push(`${name}: ${resultLift}, not significant (${confidence} confidence).`);
      }
    }
  }

  switch (decision.code) {
    case 'SHIP':
      bullets.push('Nothing got significantly worse. Safe to ship.');
      break;
    case 'SHIP_WITH_TRADEOFFS':
      bullets.push('Decide whether the trade-off is acceptable before shipping.');
      break;
    case 'DO_NOT_SHIP':
      bullets.push(`Do not ship ${decision.variantName}.`);
      break;
    case 'COLLECTING':
      bullets.push(
        decision.remainingSessions !== undefined && decision.remainingSessions > 0
          ? `Keep running — roughly ${formatVisitors(decision.remainingSessions)} more visitors are needed before a reliable call. Do not call it yet.`
          : 'Keep running — not enough visitors yet for a reliable call. Do not call it yet.',
      );
      break;
    case 'INCONCLUSIVE': {
      const detectable = primary ? getDetectableRelativeEffect(primary) : null;
      bullets.push(
        detectable !== null
          ? `Keep running or call it a draw — the experiment can currently only detect a ${formatEffectSize(detectable)} effect on ${primaryName}.`
          : 'Keep running or call it a draw. Do not ship on this evidence.',
      );
      break;
    }
    case 'INVALID_SRM':
      bullets.push('Check the rule setup and targeting before reading anything into the results.');
      break;
  }

  return { severity: display.severity, title, bullets };
}

/** The metric a decision is judged on, in the order the results arrive. */
export function getMetricsInOrder(significance: MarketingRouterSignificance): MarketingRouterMetricKey[] {
  const seen = new Set<MarketingRouterMetricKey>();
  const ordered: MarketingRouterMetricKey[] = [];
  for (const result of significance.results) {
    if (!seen.has(result.metric)) {
      seen.add(result.metric);
      ordered.push(result.metric);
    }
  }
  return ordered;
}

export type LiftScale = { min: number; max: number };

/**
 * A symmetric relative-lift axis wide enough to show every variation's CI
 * and detectable band on one metric, snapped to a round number so the ticks
 * read cleanly. Shared by all rows of a table, so bars are comparable.
 */
export function getLiftScale(results: MarketingRouterMetricResult[]): LiftScale {
  const candidates = results
    .flatMap((result) => [
      Math.abs(result.relativeLift ?? 0),
      ...(result.relativeCi95 ?? []).map(Math.abs),
      getDetectableRelativeEffect(result) ?? 0,
    ])
    .filter(Number.isFinite);
  const extent = Math.max(0.2, ...candidates) * 1.15;
  const step = extent > 1 ? 0.5 : extent > 0.4 ? 0.2 : 0.1;
  const max = Math.ceil(extent / step) * step;
  return { min: -max, max };
}

/** Where a relative lift sits on the axis, as a percentage of its width. */
export function toLiftScalePercent(value: number, scale: LiftScale): number {
  const clamped = Math.min(Math.max(value, scale.min), scale.max);
  return ((clamped - scale.min) / (scale.max - scale.min)) * 100;
}
